import { initAnalytics } from './analytics/analytics';
import { initAds } from './ads/ads';
import { initRealUserMonitoring } from './realUserMonitoring/realUserMonitoring';
import {
  getAnalyticsConsent,
  getGdprApplies,
  readPublisherConsentCookie,
  readVendorsConsentCookie,
  removeNullParams,
  updateGdprApplies,
} from './init.utils';
import { getPageURL } from './pageURL/pageURL';
import { markPerformance } from './performance/index';
import { getIdentifier } from './userIdentifier';
import { ErrorReporter } from './errorReporter.type';
import { AdParams, AdSlots } from './ads/ads.types';
import { AnalyticsParams } from './analytics/analytics.types';
import { registerInitAdsForGooglePersonalizationData } from './ads/registerInitAdsForGooglePersonalizationData';

export interface StateAdParams {
    language: string;
    category: string | null;
    pageType: string;
    articleId?: string;
    articleTemplate?: Array<string>;
    channels?: Array<string>;
    contentTags?: Array<string>;
    adSlots: AdSlots;
    headerBidderPublisherID: string;
    mainMedia?: string;
    commercialTags?: Array<string>;
    dfpCategory?: string;
    dfpProperty?: string;
}

export interface StateAnalyticsParams {
    category: string | null;
    articleAuthor?: string;
    articleTitle?: string;
    articlePublicationTime?: string;
    property?: string;
    countryCode: string;
    articleContentCount?: { [key: string]: number };
    articleWordCount?: number;
}

export interface ClientParams {
    platform: string;
    trafficSource: string;
    trafficSourceAndId: string;
    trafficId: string;
}

export type CoreParams = StateAdParams & StateAnalyticsParams;

const pageURL = getPageURL();
const monitoringEndpoint = 'https://rum.minutemediaservices.com/';

export const createAdParameters = ({
  language,
  category,
  pageType,
  articleId,
  articleTemplate,
  channels,
  contentTags,
  mainMedia,
  commercialTags,
  dfpProperty,
  platform,
  trafficSource,
  trafficSourceAndId,
}: StateAdParams & ClientParams) => removeNullParams({
  language,
  platform,
  category,
  contentTags,
  pageType,
  test: pageURL.test,
  affiliateId: pageURL.affiliateId,
  articleId,
  articleTemplate,
  distributionChannels: channels,
  mmUserIdentifier: getIdentifier(),
  trafficSource,
  trafficSourceAndId,
  mainMedia,
  commercialTags,
  dfpProperty,
});

export const createAnalyticsParametersWithAdParams = (adParameters: AdParams, analyticsParams: AnalyticsParams) => ({
  ...adParameters,
  ...analyticsParams,
});

export const createAnalyticsParams = ({
  category,
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  property,
  countryCode,
  articleContentCount,
  articleWordCount,
  trafficId,
}: StateAnalyticsParams & ClientParams) => ({
  pageIdentifier: category,
  geoDetected: countryCode,
  GA_event_cookieConsent: readVendorsConsentCookie(),
  analyticsConsent: getAnalyticsConsent(readPublisherConsentCookie()),
  markupFormat: 'web',
  gdprApplies: getGdprApplies(),
  trafficReferrer: document.referrer,
  trafficId,
  articleAuthor,
  articleTitle,
  articlePublicationTime,
  property,
  articleContentCount,
  articleWordCount,
});

const iniAdsCallback = (adSlots: AdSlots, params: AdParams, adsCategory: string, headerBidderPublisherID: string, errorProvider: ErrorReporter, shouldNotPersonalizeAds: boolean) => {
  try {
    initAds(adSlots, params, adsCategory, headerBidderPublisherID, errorProvider, shouldNotPersonalizeAds);
    markPerformance('init-ads');
  } catch (e) {
    errorProvider.reportException(e);
  }
};

export function init(params: CoreParams & ClientParams, errorProvider: ErrorReporter) {
  const { pageType, adSlots, headerBidderPublisherID, countryCode, category, dfpCategory } = params;
  const adParameters = createAdParameters(params);
  const adsCategory = dfpCategory || category;

  initRealUserMonitoring(countryCode, pageType, monitoringEndpoint, errorProvider);

  try {
    initAnalytics(createAnalyticsParametersWithAdParams(adParameters, createAnalyticsParams(params)));
    markPerformance('init-analytics');
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    if (adSlots) {
      registerInitAdsForGooglePersonalizationData(adSlots, adParameters, adsCategory!, headerBidderPublisherID, errorProvider, iniAdsCallback);
    }
  } catch (e) {
    errorProvider.reportException(e);
  }
  try {
    updateGdprApplies();
  } catch (e) {
    errorProvider.reportException(e);
  }
}
